import { useCallback, useState } from 'react';
import { useAuthenticatedFetchWithWorkspace } from '../Keycloak/useAuthenticatedFetchWithWorkspace';
import { useUser } from '../../context/UserContext';

interface ExtractPersonQueryParams {
    taskId: string;
    queryId: string | number;
    notify: string[];
    fields: string[];
}

interface ExtractPersonQueryResponse {
    status?: string;
    message?: string;
    taskId?: string;
}

const DEFAULT_ERROR = "Erreur lors de la demande d'export";

const readJsonMessage = (message: string): string | undefined => {
    const jsonStart = message.indexOf('{');
    if (jsonStart < 0) return undefined;
    try {
        const parsed = JSON.parse(message.slice(jsonStart));
        return [parsed?.message, parsed?.error].find(
            (candidate): candidate is string => typeof candidate === 'string' && candidate.trim() !== ''
        );
    } catch {
        return undefined;
    }
};

const toExportErrorMessage = (err: unknown): string =>
    err instanceof Error ? readJsonMessage(err.message) ?? (err.message || DEFAULT_ERROR) : DEFAULT_ERROR;

export const useExtractPersonQuery = () => {
    const { authFetch, isReady } = useAuthenticatedFetchWithWorkspace();
    const { selectedWorkspace } = useUser();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const extractPersonQuery = useCallback(async ({ taskId, queryId, notify, fields }: ExtractPersonQueryParams): Promise<void> => {
        if (!isReady || !taskId || !queryId || notify.length === 0) return;

        setLoading(true);
        setError(null);
        const numericQueryId = Number(queryId);

        try {
            const response: ExtractPersonQueryResponse | null = await authFetch('/putExtractPersonQuery', {
                method: 'PUT',
                body: JSON.stringify({
                    taskId,
                    notify,
                    enseigne: selectedWorkspace?.volumeName,
                    queryId: Number.isNaN(numericQueryId) ? queryId : numericQueryId,
                    fields,
                }),
            });
            if (!response) throw new Error("Réponse invalide lors de la demande d'export");
            if (response.status?.toUpperCase() === 'ERROR') throw new Error(response.message || DEFAULT_ERROR);
        } catch (err) {
            const message = toExportErrorMessage(err);
            setError(message);
            throw new Error(message);
        } finally {
            setLoading(false);
        }
    }, [authFetch, isReady, selectedWorkspace?.volumeName]);

    const reset = useCallback(() => setError(null), []);

    return { extractPersonQuery, loading, error, reset };
};
